/* eslint-disable no-underscore-dangle */
import { apiPrefix } from 'src/settings';
import { logError } from 'src/loggingManager';
import { parseError } from 'src/util/misc';
import { retrievePlatformId } from './productClient';
import { LinkInputType, LinkResponseType, LinkType } from 'src/types/link';
import { AssetSettings, LinkInputType as SdkLinkInputType } from '@rendering/web-client';
import { APIError } from 'src/types/index';
import { getSdk } from 'src/sdk';

const ASSET_ENDPOINT = `https://${apiPrefix}assets.documents.cimpress.io/v3/assets`;

/**
 * The values needed for linking an asset to a product
 * @typedef {Object} LinkInput
 * @property {string} ReferenceId A product sku to associate an asset with
 * @property {string[]} [SubReferenceIds] List of surfaces of the product that the asset is associated with
 * @property {Object} [Attributes] Product variables for the sku
 * @property {string} Purpose - The purpose of the asset (design, product, merchandising)
 * @property {string} SubPurpose The sub purpose of the asset. For design scenes sub purposes include underlay and overlay
 * @property {string[]} [Tags] List of tags for the asset
 */

/**
 * Generates a URI to an asset's link
 * @param options
 * @param options.assetId - the ID of the asset
 * @param options.linkId - the ID of the link
 * @returns
 */
export function generateLinkUri({ assetId, linkId }: { assetId: string; linkId: string }) {
    return `${ASSET_ENDPOINT}/${assetId}/links/${linkId}`;
}

/**
 * Links a scene to a product.
 * @param options
 * @param options.assetId - The ID of the asset being linked
 * @param options.linkInput - The object containing link properties.
 * @returns - The promise of the request.
 */
export function createLink({ assetId, linkInput }: { assetId: string; linkInput: LinkInputType }) {
    const input: SdkLinkInputType = {
        ...linkInput,
        referenceIdVersion: linkInput.referenceIdVersion ?? 0,
        linkPurposes: linkInput.linkPurposes?.map(({ purpose, subPurpose }) => ({
            purpose,
            subPurpose: subPurpose || '',
        })),
    };
    return getSdk()
        .AssetLinks.create(assetId, input)
        .catch((e) => {
            logError(`create asset link failed! ${parseError(e)} ${JSON.stringify(linkInput)}`);
            throw e;
        });
}

export function deleteLink({ assetId, linkId }: { assetId: string; linkId: string }) {
    return getSdk()
        .AssetLinks.remove(assetId, linkId)
        .catch((e) => {
            logError(`delete asset link failed! ${parseError(e)} asset: ${assetId}; link: ${linkId}`);
            throw e;
        });
}

/**
 *  Links a scene to a product.
 * @param options
 * @param options.linkId - The existing link id.
 * @param options.assetId - The ID of the asset
 * @param options.linkInput - The object containing link properties.
 * @returns - The promise of the request.
 */
export function updateLink({
    linkInput,
    assetId,
    linkId,
}: {
    linkId: string;
    linkInput: LinkInputType;
    assetId: string;
}) {
    const input = {
        ...linkInput,
        linkPurposes: linkInput.linkPurposes?.map(({ purpose, subPurpose }) => ({
            purpose,
            subPurpose: subPurpose || '',
        })),
    };
    return getSdk()
        .AssetLinks.update(assetId, linkId, input)
        .catch((e) => {
            logError(`update asset link failed! ${parseError(e)} ${assetId} ${linkId} ${JSON.stringify(linkInput)}`);
            throw e;
        });
}

export function mapLink(link: LinkResponseType): Promise<LinkType> {
    const output: LinkType = {
        id: link.id,
        referenceId: link.referenceId,
        referenceIdVersion: link.referenceIdVersion || 0,
        sceneId: link.assetId,
        linkId: link.id,
        sku: link.referenceId,
        skuVersion: link.referenceIdVersion || 0,
        merchantSku: link.referenceId,
        merchantSkuVersion: link.referenceIdVersion || 0,
        merchantVariables: link.attributes || {},
        variableConfigurationId: link.variableConfigurationId,
        purpose: link.purpose,
        linkPurposes: link.linkPurposes,
        tenant: link.tenant,
        subPurpose: link.subPurpose,
        variables: link.attributes || {},
        tags: link.tags || [],
        surfaces: link.subReferenceIds ? link.subReferenceIds.map((id) => ({ id })) : [],
        view: link?.view,
        _links: link._links,
    };

    if (link.referenceId && link.referenceId.startsWith('PRD')) {
        try {
            return retrievePlatformId({
                sku: link.referenceId,
                skuVersion: link.referenceIdVersion,
                attributes: link.attributes,
            }).then(({ merchantSku, merchantVersion, merchantVariables }) => {
                output.merchantSku = merchantSku;
                output.merchantSkuVersion = merchantVersion;
                output.merchantVariables = merchantVariables;
                return output;
            });
        } catch (error) {
            logError(`Failed to retrieve the VP platform id: ${link.referenceId} ${error}`);
            throw error;
        }
    }

    return Promise.resolve(output);
}

export function mapLinks(results: LinkResponseType[]) {
    return Promise.all(results.map(mapLink));
}

/**
 * Gets all links for an asset
 * @param options
 * @param options.assetId The ID of the asset
 * @returns - The promise of the request.
 */
export function getLinksForAsset({ assetId }: { assetId: string }) {
    return getSdk()
        .AssetLinks.getLinksByAssetId(assetId, 100, undefined, { 'Cache-Control': 'no-cache' })
        .then((response) => mapLinks((response._embedded.item || []) as LinkResponseType[]))
        .catch((e) => {
            logError(`query asset error (assetId=${assetId}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Gets all settings for tenant
 * @param tenantType
 * @param tenantId
 * @returns - The promise of the request.
 */
export async function getPurposeSettings(tenantType: string, tenantId: string) {
    try {
        const settingsInfo = await getSdk().Settings.getSettings('assetLinkingSettings', tenantType, tenantId);
        if (settingsInfo._embedded?.item?.length) {
            const { subPurposes, tags, views = [] }: AssetSettings = settingsInfo._embedded.item[0].settings.settings;
            return { subPurposes, tags, views };
        }
        return [];
    } catch (error: unknown) {
        logError(
            `query asset error (tenantType=${tenantType}, tenantId=${tenantId}): ${parseError(error as APIError)}`,
        );
        throw error;
    }
}
